import { Portfolio } from "@/types/Portfolio";
import { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import type { SearchHeadlinesParams } from "@/widget-components/newsquawk-headlines/api/types";

// Watchlist type detection
export type WatchlistType = "ticker-only" | "keyword-only" | "mixed";

export const getWatchlistType = (portfolio: Portfolio): WatchlistType => {
  const hasTickers = portfolio.tickers.length > 0;
  const hasKeywords = (portfolio.keywords?.length ?? 0) > 0;

  if (hasTickers && !hasKeywords) return "ticker-only";
  if (!hasTickers && hasKeywords) return "keyword-only";
  return "mixed";
};

// Groups portfolios by their type
export const groupPortfoliosByType = (portfolios: Portfolio[]) => {
  return {
    tickerOnly: portfolios.filter((p) => getWatchlistType(p) === "ticker-only"),
    keywordOnly: portfolios.filter((p) => getWatchlistType(p) === "keyword-only"),
    mixed: portfolios.filter((p) => getWatchlistType(p) === "mixed"),
  };
};

// Extracts unique keywords from category queries, filtering out empty values
export const extractKeywordsFromQueries = (
  queries: CategoryQuery[]
): string[] => {
  return queries.map((f) => f.searchKeyword).filter(Boolean) as string[];
};

// Extracts portfolio IDs from category queries as strings
export const extractPortfolioIdsFromQueries = (
  queries: CategoryQuery[]
): string[] => {
  return queries
    .map((f) => f.portfolioIds)
    .flatMap((id) => String(id))
    .filter(Boolean);
};

// Extracts and deduplicates tickers from a list of portfolios
export const extractTickersFromPortfolios = (
  portfolios: Portfolio[]
): string[] => {
  const allTickers = portfolios.flatMap((p) => p.tickers);
  return Array.from(new Set(allTickers));
};

// Extracts and deduplicates keywords from a list of portfolios
export const extractKeywordsFromPortfolios = (
  portfolios: Portfolio[]
): string[] => {
  const allKeywords = portfolios.flatMap((p) => p.keywords || []);
  return Array.from(new Set(allKeywords));
};

// Builds a formatted search title from selected filters and portfolios, joining with " + "
export const buildSearchTitle = (params: {
  selectedAdvancedFilters: CategoryQuery[];
  selectedFilterPortfolio: Portfolio[];
  selectedSourceLabels?: string[];
  defaultTitle?: string;
}): string => {
  const { selectedAdvancedFilters, selectedFilterPortfolio, selectedSourceLabels, defaultTitle } =
    params;

  const titleParts: string[] = [];

  // Add category filter names
  if (selectedAdvancedFilters && selectedAdvancedFilters.length > 0) {
    const filterNames = selectedAdvancedFilters
      .map((f) => f.queryName)
      .join(", ");
    titleParts.push(filterNames);
  }

  // Add portfolio filter names
  if (selectedFilterPortfolio && selectedFilterPortfolio.length > 0) {
    const selectedPortfolioNames = selectedFilterPortfolio
      .map((p) => p.name)
      .join(", ");
    if (selectedPortfolioNames) {
      titleParts.push(selectedPortfolioNames);
    }
  }

  // Add source filter names
  if (selectedSourceLabels && selectedSourceLabels.length > 0) {
    const sourceNames = selectedSourceLabels.join(", ");
    titleParts.push(sourceNames);
  }

  // Build final title
  if (titleParts.length > 0) {
    return `${defaultTitle} (Search: ${titleParts.join(" + ")})`;
  }

  return defaultTitle ?? "Firehose Widget";
};

// Maps portfolio ID lists to portfolio objects, combining and deduplicating IDs from multiple sources
// portfolios parameter should include all portfolios (both user and prebuilt) from the API
export const mapPortfolioIdsToPortfolios = (
  portfolios: Portfolio[],
  ...portfolioIdLists: string[][]
): Portfolio[] => {
  const allPortfolioIds = Array.from(new Set(portfolioIdLists.flat())).map(
    (id) => Number(id)
  );

  const portfolioIdsSet = new Set(allPortfolioIds);

  return portfolios.filter((portfolio) =>
    portfolioIdsSet.has(portfolio.id)
  );
};

// Transforms SearchHeadlinesParams into the API request payload format
export const transformSearchParams = (params: SearchHeadlinesParams) => {
  const {
    query = "",
    rows = 10,
    exactMatch = false,
    advancedFilter,
    portfolios,
    tags = [],
    cursor,
    dateRange,
  } = params;

  const tagIds = [...(advancedFilter?.categories || []), ...tags];

  const allPortfolios = [
    ...(portfolios || []),
    ...(advancedFilter?.portfolios || []),
  ];

  const tickers = extractTickersFromPortfolios(allPortfolios);
  const keywords = extractKeywordsFromPortfolios(allPortfolios);

  const finalSearchTerm = [query, ...keywords, advancedFilter?.searchKeyword]
    .filter(Boolean)
    .join(" OR ");

  const filterMode = advancedFilter?.filterMode || "AND";
  const keywordOp = advancedFilter?.keywordOp || "OR";

  return {
    search_term: finalSearchTerm,
    limit: rows,
    tags: tagIds,
    tickers,
    exact_match: exactMatch,
    filter_mode: filterMode.toUpperCase(),
    keyword_op: keywordOp?.toUpperCase(),
    cursor,
    date_range: {
      start_date: dateRange?.startDate,
      end_date: dateRange?.endDate,
    },
  };
};
